import { UnknownAction } from "@reduxjs/toolkit";
import {
  Contact,
  ContactShare,
  FetchAssetAnalyticsForContactRequest,
  FetchContactShareSectionRequest,
  FetchContentsForShareRequest,
  FetchingAssetAnalyticsForContactSucceeded,
  FetchingContactFailed,
  FetchingContactShareSectionFailed,
  FetchingContactShareSectionSucceeded,
  FetchingContactSucceeded,
  FetchingContentsForShareFailed,
  FetchingContentsForShareSucceeded,
  FetchingSharesForContactSucceeded,
  updatingContactFailed,
  updatingContactSucceeded,
} from "src/types/contact";
import {
  FETCH_CONTACT_ASSET_ANALYTICS_REQUEST,
  FETCH_CONTACT_REQUEST,
  FETCH_CONTACT_SHARE_SECTION_REQUEST,
  FETCH_CONTENTS_FOR_SHARE,
  FETCH_SHARES_FOR_CONTACT_REQUEST,
  FETCHING_CONTACT_ASSET_ANALYTICS_SUCCEEDED,
  FETCHING_CONTACT_FAILED,
  FETCHING_CONTACT_SHARE_SECTION_FAILED,
  FETCHING_CONTACT_SHARE_SECTION_SUCCEEDED,
  FETCHING_CONTACT_SUCCEEDED,
  FETCHING_CONTENTS_FOR_SHARE_FAILED,
  FETCHING_CONTENTS_FOR_SHARE_SUCCEEDED,
  FETCHING_PREVIEW_ASSET,
  FETCHING_PREVIEW_ASSET_SUCCEEDED,
  FETCHING_SHARES_FOR_CONTACT_REQUEST_FAILED,
  FETCHING_SHARES_FOR_CONTACT_REQUEST_SUCCEEDED,
  INVALIDATE_CONTACT_UPDATIONS,
  UPDATE_CONTACT_REQUEST,
  UPDATING_CONTACT_FAILED,
  UPDATING_CONTACT_SUCCEEDED,
} from "../actions/actionTypes";
import { FetchPreviewAsset, FetchPreviewAssetResponse } from "src/types/assets";

interface SharesState {
  loading: boolean;
  items: ContactShare[];
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
  };
  error: any;
}

interface ContacState {
  loading: boolean;
  contact: Contact | null;
  error?: any;
  shares: SharesState;
}

const initialState = {
  loading: false,
  contact: null,
  shares: {
    loading: false,
    items: [],
    pagination: {
      currentPage: 0,
      totalPages: 0,
      recordsPerPage: 0,
    },
    error: null,
  },
};

export function contactReducer(
  state: ContacState = initialState,
  action: UnknownAction,
): ContacState {
  switch (action.type) {
    case FETCH_CONTACT_REQUEST: {
      return {
        ...state,
        loading: true,
      };
    }
    case FETCHING_CONTACT_SUCCEEDED: {
      const { response } = action.payload as FetchingContactSucceeded;
      return {
        ...state,
        loading: false,
        contact: response,
      };
    }

    case FETCHING_CONTACT_FAILED: {
      const { error } = action.payload as FetchingContactFailed;
      return {
        ...state,
        loading: false,
        error,
      };
    }

    case FETCH_SHARES_FOR_CONTACT_REQUEST: {
      return {
        ...state,
        shares: {
          ...state.shares,
          loading: true,
        },
      };
    }

    case FETCHING_SHARES_FOR_CONTACT_REQUEST_SUCCEEDED: {
      const { response } = action.payload as FetchingSharesForContactSucceeded;
      let records = response.records;
      if (response.currentPage > 0) {
        records = [...state.shares.items, ...records];
      }
      return {
        ...state,
        shares: {
          ...state.shares,
          loading: false,
          items: records,
          pagination: {
            ...state.shares.pagination,
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
          },
        },
      };
    }

    case FETCHING_SHARES_FOR_CONTACT_REQUEST_FAILED: {
      const { error } = action.payload as FetchingContactFailed;
      return {
        ...state,
        shares: {
          ...state.shares,
          loading: false,
          error,
        },
      };
    }

    case FETCH_CONTENTS_FOR_SHARE: {
      const { shareId } = action.payload as FetchContentsForShareRequest;
      const shares = state.shares.items.map((share) => {
        if (share.id === shareId) {
          return {
            ...share,
            fetchingChildren: true,
          };
        }
        return share;
      });

      return {
        ...state,
        shares: {
          ...state.shares,
          items: shares,
        },
      };
    }

    case FETCHING_CONTENTS_FOR_SHARE_SUCCEEDED: {
      const { shareId, response } =
        action.payload as FetchingContentsForShareSucceeded;
      const shares = state.shares.items.map((share) => {
        if (share.id === shareId) {
          return {
            ...share,
            fetchingChildren: false,
            fetchedChildren: true,
            ...response,
          };
        }
        return share;
      });

      return {
        ...state,
        shares: {
          ...state.shares,
          items: shares,
        },
      };
    }

    case FETCHING_CONTENTS_FOR_SHARE_FAILED: {
      const { shareId, error } =
        action.payload as FetchingContentsForShareFailed;
      const shares = state.shares.items.map((share) => {
        if (share.id === shareId) {
          return {
            ...share,
            fetchingChildren: false,
            fetchedChildren: false,
            error,
          };
        }
        return share;
      });

      return {
        ...state,
        shares: {
          ...state.shares,
          items: shares,
        },
      };
    }

    case FETCH_CONTACT_ASSET_ANALYTICS_REQUEST: {
      const { shareId, assetId, sectionId } =
        action.payload as FetchAssetAnalyticsForContactRequest;
      const shares = state.shares.items.map((share) => {
        if (share.id === shareId) {
          if (!sectionId && share.assets) {
            return {
              ...share,
              assets: share.assets?.map((asset) => {
                if (asset.id === assetId) {
                  return {
                    ...asset,
                    fetchingAnalytics: true,
                  };
                }
                return asset;
              }),
            };
          } else if(sectionId && share.sections) {
            return {
              ...share,
              sections: share.sections?.map((section) => {
                if (section.id === sectionId) {
                  return {
                    ...section,
                    assets: section.assets.map((asset) => {
                      if (asset.id === assetId) {
                        return {
                          ...asset,
                          fetchingAnalytics: true,
                          fetchedAnalytics: false,
                        };
                      }
                      return asset;
                    }),
                  };
                }
                return section;
              }),
            };
          } else {
            return share;
          }
        }
        return share;
      });

      return {
        ...state,
        shares: {
          ...state.shares,
          items: shares,
        },
      };
    }

    case FETCHING_CONTACT_ASSET_ANALYTICS_SUCCEEDED: {
      const { shareId, assetId, sectionId, analytics } =
        action.payload as FetchingAssetAnalyticsForContactSucceeded;
      const shares = state.shares.items.map((share) => {
        if (share.id === shareId) {
          if (!sectionId && share.assets) {
            return {
              ...share,
              assets: share.assets?.map((asset) => {
                if (asset.id === assetId) {
                  return {
                    ...asset,
                    fetchingAnalytics: false,
                    fetchedAnalytics: true,
                    analytics,
                  };
                }
                return asset;
              }),
            };
          } else if(sectionId && share.sections) {
            return {
              ...share,
              sections: share.sections?.map((section) => {
                if (section.id === sectionId) {
                  return {
                    ...section,
                    assets: section.assets.map((asset) => {
                      if (asset.id === assetId) {
                        return {
                          ...asset,
                          fetchingAnalytics: false,
                          fetchedAnalytics: true,
                          analytics,
                        };
                      }
                      return asset;
                    }),
                  };
                }
                return section;
              }),
            };
          } else {
            return share;
          }
        }
        return share;
      });

      return {
        ...state,
        shares: {
          ...state.shares,
          items: shares,
        },
      };
    }
    case FETCH_CONTACT_SHARE_SECTION_REQUEST: {
      const { shareId, sectionId } =
        action.payload as FetchContactShareSectionRequest;
      const shares = state.shares.items.map((share) => {
        if (share.id === shareId) {
          return {
            ...share,
            sections: share.sections?.map((section) => {
              if (section.id === sectionId) {
                return {
                  ...section,
                  loading: true,
                  loaded: false,
                };
              }
              return section;
            }),
          };
        }
        return share;
      });

      return {
        ...state,
        shares: {
          ...state.shares,
          items: shares,
        },
      };
    }

    case FETCHING_CONTACT_SHARE_SECTION_SUCCEEDED: {
      const { shareId, sectionId, response } =
        action.payload as FetchingContactShareSectionSucceeded;
      const shares = state.shares.items.map((share) => {
        if (share.id === shareId) {
          return {
            ...share,
            sections: share.sections?.map((section) => {
              if (section.id === sectionId) {
                return {
                  ...section,
                  loading: false,
                  loaded: true,
                  ...response,
                };
              }
              return section;
            }),
          };
        }
        return share;
      });

      return {
        ...state,
        shares: {
          ...state.shares,
          items: shares,
        },
      };
    }

    case FETCHING_CONTACT_SHARE_SECTION_FAILED: {
      const { shareId, sectionId, error } =
        action.payload as FetchingContactShareSectionFailed;
      const shares = state.shares.items.map((share) => {
        if (share.id === shareId) {
          return {
            ...share,
            sections: share.sections?.map((section) => {
              if (section.id === sectionId) {
                return {
                  ...section,
                  loading: false,
                  loaded: false,
                  error,
                };
              }
              return section;
            }),
          };
        }
        return share;
      });

      return {
        ...state,
        shares: {
          ...state.shares,
          items: shares,
        },
      };
    }

    case FETCHING_PREVIEW_ASSET: {
      const { id } = action.payload as FetchPreviewAsset;

      const items = state.shares.items.map((share) => {
        const sections = share.sections || [];
        const updatedSections = sections.map((section) => {
          const sectionassets = section.assets || [];
          const assets = sectionassets.map((asset) => {
            if (asset.id == id) {
              return Object.assign({}, asset, {
                ...asset,
                preview: {
                  loading: true,
                  item: null,
                  fetch: false,
                },
              });
            }

            return asset;
          });
          return {
            ...section,
            assets: assets,
          };
        });

        let nonSectionedAssets = share.assets || [];
        nonSectionedAssets = nonSectionedAssets.map((asset) => {
          if (asset.id == id) {
            return Object.assign({}, asset, {
              ...asset,
              preview: {
                loading: true,
                item: null,
                fetch: false,
              },
            });
          }
          return asset;
        });

        return {
          ...share,
          sections: updatedSections,
          assets: nonSectionedAssets,
        };
      });

      return {
        ...state,
        shares: {
          ...state.shares,
          items: items,
        },
      };
    }

    case FETCHING_PREVIEW_ASSET_SUCCEEDED: {
      const { id, response } = action.payload as FetchPreviewAssetResponse;
      const items = state.shares.items.map((share) => {
        const sections = share.sections || [];
        const updatedSections = sections.map((section) => {
          const sectionassets = section.assets || [];
          const assets = sectionassets.map((asset) => {
            if (asset.id == id) {
              return Object.assign({}, asset, {
                ...asset,
                preview: {
                  loading: false,
                  item: response,
                  fetch: true,
                },
              });
            }

            return asset;
          });
          return {
            ...section,
            assets: assets,
          };
        });

        let nonSectionedAssets = share.assets || [];
        nonSectionedAssets = nonSectionedAssets.map((asset) => {
          if (asset.id == id) {
            return Object.assign({}, asset, {
              ...asset,
              preview: {
                loading: false,
                item: response,
                fetch: true,
              },
            });
          }
          return asset;
        });

        return {
          ...share,
          sections: updatedSections,
          assets: nonSectionedAssets,
        };
      });

      return {
        ...state,
        shares: {
          ...state.shares,
          items: items,
        },
      };
    }

    case UPDATE_CONTACT_REQUEST: {
      return {
        ...state,
        contact : {
          ...state.contact,
          updating: true,
        },
      };
    }

    case UPDATING_CONTACT_SUCCEEDED: {
      const { response } = action.payload as updatingContactSucceeded;
      return {
        ...state,
        contact : {
          ...state.contact,
          ...response,
          updating: false,
        },
      };
    }

    case UPDATING_CONTACT_FAILED: {
      const { error } = action.payload as updatingContactFailed;
      return {
        ...state,
        contact : {
          ...state.contact,
          updating: false,
        },
        error,
      };
    }

    case INVALIDATE_CONTACT_UPDATIONS: {
      return {
        ...state,
        contact : {
          ...state.contact,
          updating: null,
        },
      };
    }
    default:
      return state;
  }
}
